import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, vShow as _vShow, withDirectives as _withDirectives, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, resolveDirective as _resolveDirective, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "lp-wave-content" }
const _hoisted_2 = { class: "lp-wave-login" }
const _hoisted_3 = { class: "ly-row login-container ly-center-center" }
const _hoisted_4 = {
  class: /*@__PURE__*/_normalizeClass(['login-content', 'ly-col', 'ly-center-center'])
}
const _hoisted_5 = { class: "login-content-form" }
const _hoisted_6 = { class: "lp-load-fadein" }
const _hoisted_7 = {
  key: 1,
  class: "mt-3"
}
const _hoisted_8 = {
  key: 2,
  class: "msg msg-error w-100 text-center"
}
const _hoisted_9 = {
  key: 3,
  class: "stroked-text-container"
}
const _hoisted_10 = {
  key: 5,
  class: "mt-1"
}

import NewLoginForm from './components/NewLoginForm.vue'
import GoogleLoginButton from './components/GoogleLoginButton.vue'
import { inject, onMounted, ref, Ref, computed, onBeforeMount } from 'vue'
import { ServiceNames } from '@/types/serviceNames'
import { LMS, CONNECTIONS, SECURE_PAYMENTS } from '@/consts/serviceNames'
import SamlLoginButton from './components/SamlLoginButton.vue'
import { useConfig, useConfigSync } from '@/config/useConfig'
import { Redirect } from '@/types/redirect'
import { LoginUIMethods } from '@/types/interfaces'


export default /*@__PURE__*/_defineComponent({
  __name: 'NewLoginView',
  setup(__props) {

const submitting: Ref<boolean> = ref(false)
const submittingIdp: Ref<boolean> = ref(false)
const errorMessage: Ref<string> = ref('')
const serviceSelector: Ref<ServiceNames> = ref(LMS)
const loginUiMethods: Ref<LoginUIMethods> = ref({
  username_password_login: false,
  google_login: false,
  forgot_password: false
})
const showFlags: Ref<{ secure_payments_btn: boolean, connections_btn: boolean }> = ref({
  secure_payments_btn: true,
  connections_btn: true
})

const usernamePasswordLogin = inject('usernamePasswordLogin') as (username: string, password: string, selectedService: ServiceNames) => Promise<void>
const googleLogin = inject('googleLogin') as (googleToken: string, redirectUrlAfterLogin: string) => Promise<void>
const samlLogin = inject('samlLogin') as (customProviderUrl?: string) => Promise<{ idp_url: string }>
const redirect = inject('redirect') as Redirect

const loadUIMethods = async () => {
  const config = await useConfig()
  loginUiMethods.value = config.login_ui_methods
  showFlags.value = {
    secure_payments_btn: !config.hide_flags?.secure_payments_btn,
    connections_btn: !config.hide_flags?.connections_btn
  }
}

const showStrokedTextContainer = computed(() => {
  return showLoginForm.value && (showGoogleBtn.value || showSamlBtn.value)
})

const showGoogleBtn = computed(() => {
  const isActive = loginUiMethods.value.google_login === true || loginUiMethods.value.google_login === 'true'
  return isActive && serviceSelector.value === 'LMS'
})

const showSamlBtn = computed(() => {
  const config = useConfigSync()
  const isActive = config?.saml?.loginActive === true || config?.saml?.loginActive === 'true'
  return isActive && serviceSelector.value === 'LMS'
})

const showLoginForm = computed(() => {
  return loginUiMethods.value.username_password_login === true || loginUiMethods.value.username_password_login === 'true' ||
    serviceSelector.value !== 'LMS'
})

const triggerSamlLogin = async () => {
  const config = await useConfig()
  const host = window.location.hostname
  const samlHostname = config.saml.samlURL.replace(/(http:\/\/)|(https:\/\/)+/, '')

  // If it does. The SAML payload and SAML url are comming from LMS.
  if (config.saml.samlURL.includes('UserIdentityProvider.samlRequest')) {
    await samlLogin(config.saml.samlURL)
    return
  }

  if (config.saml.samlURL && !samlHostname.includes(host)) {
    window.location.href = config.saml.samlURL
    return
  }

  await samlLogin()
}

const onFormSubmitted = async (params: { email: string, password: string }) => {
  if (submitting.value) return
  submitting.value = true
  errorMessage.value = ''
  try {
    await usernamePasswordLogin(params.email, params.password, serviceSelector.value)
  } catch (error: any) {
    if (error.code === 'PasswordExpired') {
      redirect('/reset')
    }
    displayError(error)
  }
  submitting.value = false
}

const onGoogleLogin = async (params: { googleToken: string }) => {
  if (submitting.value) return
  submitting.value = true
  try {
    await googleLogin(params.googleToken, serviceSelector.value)
  } catch (error: any) {
    displayError(error)
  }
  submitting.value = false
}

const onSamlLogin = async () => {
  if (submitting.value) return
  submittingIdp.value = true
  try {
    triggerSamlLogin()
  } catch (error: any) {
    displayError(error)
  }
  submitting.value = false
}

const setProvider = (provider: string) => {
  window.localStorage.setItem('provider', provider)
}

const displayError = (error: any) => {
  error.message
    ? errorMessage.value = error.message
    : errorMessage.value = 'Unexpected Error'
}

const onClickLearnMore = async () => {
  const config = await useConfig()
  window.location.href = config.sso.learn_more_hyperlink
}

const onClickGetStarted = async () => {
  const config = await useConfig()
  let getStartedLink = config.sso.get_started_hyperlink
  switch (serviceSelector.value) {
    case CONNECTIONS:
      getStartedLink = config.connections.get_started_hyperlink
      break
    case SECURE_PAYMENTS:
      getStartedLink = config.sp.get_started_hyperlink
      break
  }
  redirect(getStartedLink, true, true)
}
const onClickForgotPassword = async () => {
  const config = await useConfig()
  switch (serviceSelector.value) {
    case CONNECTIONS:
      redirect(config.connections.forgot_password_hyperlink, true, true)
      break
    case SECURE_PAYMENTS:
      redirect(config.sp.forgot_password_hyperlink, true, true)
      break
    default:
      redirect('ForgotView')
  }
}

onMounted(() => {
  setProvider(serviceSelector.value)
})

onBeforeMount(async () => {
  await loadUIMethods()
})


return (_ctx: any,_cache: any) => {
  const _directive_load_fade_in = _resolveDirective("load-fade-in")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_5, [
            _createVNode(_Transition, { name: "fade" }, {
              default: _withCtx(() => [
                _withDirectives(_createElementVNode("div", _hoisted_6, [
                  _cache[2] || (_cache[2] = _createElementVNode("div", null, [
                    _createElementVNode("h1", { class: "text-center text-black-100 font-weight-bold" }, "Sign In")
                  ], -1)),
                  (showLoginForm.value)
                    ? (_openBlock(), _createBlock(NewLoginForm, {
                        key: 0,
                        onFormSubmitted: onFormSubmitted,
                        isSubmitting: submitting.value
                      }, null, 8, ["isSubmitting"]))
                    : _createCommentVNode("", true),
                  (!showLoginForm.value && (showSamlBtn.value || showGoogleBtn.value))
                    ? (_openBlock(), _createElementBlock("div", _hoisted_7))
                    : _createCommentVNode("", true),
                  (errorMessage.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_8, _toDisplayString(errorMessage.value), 1))
                    : _createCommentVNode("", true),
                  (showStrokedTextContainer.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_9, [...(_cache[0] || (_cache[0] = [
                        _createElementVNode("div", { class: "stroked-text" }, null, -1),
                        _createElementVNode("span", null, "or", -1)
                      ]))]))
                    : _createCommentVNode("", true),
                  (showGoogleBtn.value)
                    ? (_openBlock(), _createBlock(GoogleLoginButton, {
                        key: 4,
                        class: "mt-1",
                        onGoogleLogin: onGoogleLogin
                      }))
                    : _createCommentVNode("", true),
                  (showSamlBtn.value && showGoogleBtn.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_10))
                    : _createCommentVNode("", true),
                  (showSamlBtn.value)
                    ? (_openBlock(), _createBlock(SamlLoginButton, {
                        key: 6,
                        onClick: onSamlLogin,
                        isSubmitting: submittingIdp.value
                      }, null, 8, ["isSubmitting"]))
                    : _createCommentVNode("", true),
                  _createElementVNode("div", { class: "mt-3 text-center" }, [
                    _createElementVNode("p", { class: "text-size-normal" }, [
                      _cache[1] || (_cache[1] = _createTextVNode(" Don't have an account? ", -1)),
                      _createElementVNode("a", {
                        onClick: onClickGetStarted,
                        class: "blue-link"
                      }, "Request a Demo")
                    ])
                  ])
                ], 512), [
                  [_vShow, true]
                ])
              ]),
              _: 1
            })
          ])), [
            [_directive_load_fade_in, { delay: 1000, timeBetween: 500 }]
          ])
        ])
      ])
    ])
  ]))
}
}

})